import React, { useEffect, useMemo, useState } from "react"
import { Grid, Table, Breakpoint, theme, Flex, Typography, Divider, Button, Tooltip, Dropdown, Space, Switch, Checkbox } from "antd"
import { AnyObject } from "antd/es/_util/type";

import { ColumnType } from "antd/es/table";
import ExportarComoCSV from "./ExportarComoCSV";
import { Json2CsvOptions } from "json-2-csv/lib/types";

import Utilitario from "@/Utils/Utilitario";
import { FilterOutlined } from "@ant-design/icons";
const { encontrar: find, textContent } = Utilitario;

export type Span = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 24

export type BreakpointsToSpan = Record<Breakpoint, Span>;

export type ColumnWithBreakpoints<RecordType extends AnyObject, DataIndexType extends AnyObject = RecordType> = Omit<ColumnType<RecordType>, "dataIndex"> & {
    dataIndex?: ColumnType<DataIndexType>["dataIndex"],
    breakpoints?: Partial<BreakpointsToSpan>;
    noResponsiveTitle?: boolean;
    key: React.Key;
    csv?: {
        ignore?: boolean,
        get?: (value?: RecordType) => string,
        title?: string
    },
    minWidth?: React.CSSProperties["minWidth"]
};

export interface ResponsiveAntdTableProps<RecordType extends AnyObject, DataIndexType extends AnyObject = RecordType> extends Omit<React.ComponentProps<typeof Table<RecordType>>, "children" | "columns" > {
    disableResponsiveness?: boolean;
    minColWidthIfResponsivenessDisabled?: React.CSSProperties["minWidth"]

    columns?: ColumnWithBreakpoints<RecordType, DataIndexType>[];
    addTitleOn?: Breakpoint;
    disableImplicitSorts?: boolean;

    insertSubHeader?: {title: React.ReactNode, data: React.ReactNode[], colSpan?: number}[];

    /**
     * Tamanho mínimo que as colunas onde a largura foi calculada automaticamente devem ocupar.
     * 
     * Por padrão esse valor é 2.
     */
    minGeneratedSpanWidth?: Span;
    
    /**
     * Indica se as colunas com tamanho definido devem ocupar espaços vazios
     */
    fitDefined?: boolean,

    hideColumns?: {
        enabled: boolean,
        defaultHiddenKeys?: React.Key[]
    },

    /**
     * Configuração da exportação por CSV
    * @property {string} [csv.filename='data.csv'] - O nome do arquivo a ser gerado. Por padrão, 'data.csv'.
    * @property {object} [csv.only=undefined] - Colunas a serem usadas exclusivamente pelo CSV
    * @property {object} [csv.concat=undefined] - Colunas a serem adicionadas ao CSV
    * @property {boolean} [csv.tryRendered=true] -  Utilizar os valores renderizados (default true)
    */
    csv?: 
        { enabled: false | undefined} | 
        (
            ({ 
                enabled: true,
                filename?: string,
                options?: Json2CsvOptions,
                tryRendered?: boolean,
                acquireSource?: () => Promise<RecordType[]>
            }) &
            ({ only?: Record<string, string>[] } | { concat?: (source: readonly RecordType[]) => {header: string, data: (string | undefined)[]}[] })
        )
}

const ResponsiveTableComponent: React.FC<React.HTMLAttributes<HTMLTableElement>> = (props) => {
    const style: React.CSSProperties = {
        ...props.style,
    };

    return <table className="responsive-table" {...props} style={style} />
}

type SpanPossiblyAutoGenerated = { width: number; generated: boolean };

/**
 * Divide um array de números em segmentos cuja soma não ultrapassa o limite estabelecido (threshold)
*/
function spansToRowsTheyShouldBeAt(spans: SpanPossiblyAutoGenerated[], threshold = 24): SpanPossiblyAutoGenerated[][] {
    const result: SpanPossiblyAutoGenerated[][] = [];
    let subArray: SpanPossiblyAutoGenerated[] = [];
    let currentSum = 0;

    for (const span of spans) {
        const nextSum = currentSum + span.width
        if (nextSum > threshold) {
            result.push(subArray);

            subArray = [span];
            currentSum = span.width;
        } else {
            currentSum = nextSum;
            subArray.push(span);
        }
    }

    if (subArray.length > 0) {
        result.push(subArray);
    }

    return result;
}

/**
 * Preenche os espaços vazios de uma fileira 
*/
function getSpansAsEvenRows(rows: SpanPossiblyAutoGenerated[][], fitDefinedToo: boolean = false, threshold = 24): number[]{
    return rows
        .map((_row) => {
            const row = _row.map((i) => ({...i}))

            const sum = row.reduce((p, c) => p + c.width, 0);
            let spaceLeft = threshold - (sum % threshold);
            if (spaceLeft == 0 || spaceLeft == threshold) return row;

            while (spaceLeft > 0){
                const x = row.filter((gg) => fitDefinedToo || gg.generated === true && gg.width <= threshold);

                if(x.length == 0) break;
                x.forEach((v) => {
                    if(spaceLeft <= 0) return;

                    v.width += 1;
                    spaceLeft--;
                })
            }

            return row;
        })
        .flatMap((g) => g)
        .map((g) => g.width);

}

const ResponsiveRowComponent: React.FC<React.HTMLAttributes<HTMLTableRowElement>> = (props) => {
    const appliedStyle: React.CSSProperties = props.className?.split(" ").includes("ant-table-expanded-row") ? 
        {} : 
        {
            display: props.style?.display == "none" ? props.style.display : 'grid',
            gridTemplateColumns: 'repeat(24, 1fr)',
            borderBottom: `1px solid ${theme.useToken().token.colorBorder}`,
        }

    const style: React.CSSProperties = {
        ...props.style,
        ...appliedStyle
    };

    return <tr className="responsive-row" {...props} style={style} />;
};

/**
 * Componente feito para tornar a tabela padrão do AntD responsiva ao quebrar suas colunas como grade. <br/>
 * É substituível pelo componente de tabela original. <br/>
 */
export const ResponsiveAntdTable = <RecordType extends AnyObject, DataIndexType extends AnyObject = RecordType>(
    props: ResponsiveAntdTableProps<RecordType, DataIndexType>
): JSX.Element => {
    //#region defaults
    const minGeneratedRowWidth: Span = props.minGeneratedSpanWidth ?? 2;
    //#endregion

    //#region state
    const [columnsWithClassnameIndicators, setColumnsWithClassnameIndicators] = useState<ColumnWithBreakpoints<RecordType, DataIndexType>[]>([]);
    const [isAcquiringSource, setIsAcquiringSource] = useState<boolean>(false);
    const [hiddenColumnKeys, setHiddenColumnKeys] = useState<React.Key[]>(props.hideColumns?.defaultHiddenKeys ?? []);
    const [openColumnFilter, setOpenColumnFilter] = useState<boolean>(false);
    //#endregion

    //#region dependencies
    const screenSize = Grid.useBreakpoint();
    const breakpointLevels: Record<Breakpoint, number> = { xs: 0, sm: 1, md: 2, lg: 3, xl: 4, xxl: 5 };
    const isDataSourceEmpty: boolean = props.dataSource == undefined || props.dataSource.length == 0;
    //#endregion

    //#region helper functions
    const getColKey = (k: React.Key) => `col-${k}`;

    const produceColumnSpanStyle = (classesToSpan: { className: string; spanSize: number }[]) => {
        return classesToSpan.reduce((prev, cur, idx) => {
            const { className, spanSize } = cur;
            return `${prev} .${className} { grid-column: span ${spanSize}; }`.trim();
        }, "");
    };

    const getHighestBreakpoint = (breakpoints: Partial<Record<Breakpoint, any>>): { level: number; breakpointName: Breakpoint } => {
        let max: { level: number; breakpointName: Breakpoint } = { level: 0, breakpointName: "xs" };
        Object.keys(breakpoints).forEach((k) => {
            const brk = k as Breakpoint;
            if (screenSize[brk] == true && breakpointLevels[brk] > max.level) {
                max = { level: breakpointLevels[brk], breakpointName: brk };
            }
        });

        return max;
    };

    const shouldDisplayColumn = (col: ColumnWithBreakpoints<RecordType, DataIndexType>): boolean => {
        return !hiddenColumnKeys.includes(col.key);
    };

    //#endregion

    //#region hooks
    useEffect(() => {
        const transformedColumns =
            props.columns?.filter(shouldDisplayColumn)?.map((c) => ({
                ...c,
                className: (() => {
                    const colKey = getColKey(c.key);
                    if (!c.className) return colKey;
                    if (c.className.includes(colKey)) return colKey;
                    return c.className + " " + colKey;
                })(),
            })) ?? [];

        setColumnsWithClassnameIndicators(transformedColumns);
    }, [props.columns, hiddenColumnKeys]);

    const internalDataSource = useMemo(() => {
        if (!props.dataSource) return props.dataSource;
        if (props.disableImplicitSorts) return props.dataSource;

        if (!props.dataSource[0] || typeof props.dataSource[0] !== "object" || !("ativo" in props.dataSource[0])) return props.dataSource;

        const x = [...props.dataSource];
        x.sort((a, b) => (a.ativo === true && b.ativo === false ? -1 : b.ativo === true && a.ativo === false ? 1 : 0));

        return x;
    }, [props.dataSource]);

    const columnSpanStyles = useMemo(() => {
        const a = spansToRowsTheyShouldBeAt(
            columnsWithClassnameIndicators
                .map((c) => (c.breakpoints ? c.breakpoints[getHighestBreakpoint(c.breakpoints).breakpointName] : null))
                .map((x): SpanPossiblyAutoGenerated => (x == null ? { width: minGeneratedRowWidth, generated: true } : { width: x, generated: false }))
        );

        const spans = getSpansAsEvenRows(a, props.fitDefined);

        const classesAndSpans = columnsWithClassnameIndicators.map((c, idx) => {
            const colKey = getColKey(c.key);

            let spanSizeToBeUsed = spans[idx];

            const info: Parameters<typeof produceColumnSpanStyle>[0][0] = {
                className: colKey,
                spanSize: spanSizeToBeUsed,
            };

            return info;
        });

        return produceColumnSpanStyle(classesAndSpans);
    }, [columnsWithClassnameIndicators]);
    //#endregion

    return (
        <React.Fragment>
            <div>
                {
                    <style scoped>
                        {`
                        .responsive-table.ant-table-tbody {
                            display: flex;
                            flex-direction: column;
                            gap: 0.5em; 
                        } 

                        .responsive-table.ant-table-placeholder {
                            grid-template-columns: 1fr !important;
                        }
                    `}
                    </style>
                }

                <style scoped>{columnSpanStyles}</style>

                {props.hideColumns?.enabled && (
                    <div style={{ display: "flex", flexDirection: "row-reverse" }}>
                        <Dropdown
                            open={openColumnFilter}
                            onOpenChange={(nextOpen, info) => {
                                if (info.source === "trigger" || nextOpen) {
                                    setOpenColumnFilter(nextOpen);
                                }
                            }}
                            menu={{
                                items: props.columns?.map((col) => {
                                    return {
                                        key: `control-${col.key}`,
                                        label: (
                                            <Space>
                                                <Checkbox
                                                    checked={shouldDisplayColumn(col)}
                                                    onChange={() => {
                                                        setHiddenColumnKeys(
                                                            hiddenColumnKeys.includes(col.key)
                                                                ? hiddenColumnKeys.filter((k) => k !== col.key)
                                                                : [...hiddenColumnKeys, col.key],
                                                        );
                                                    }}
                                                />
                                                {col.title?.toString()}
                                            </Space>
                                        ),
                                    };
                                })
                            }}
                            trigger={["click"]}
                        >
                            <Tooltip title={"Filtrar colunas"} placement="top">
                                <Button type="link" icon={<FilterOutlined />} />
                            </Tooltip>
                        </Dropdown>
                    </div>
                )}

                <Table<RecordType>
                    {...props}
                    dataSource={internalDataSource}
                    components={
                        props.disableResponsiveness
                            ? !props.insertSubHeader
                                ? {}
                                : {
                                      header: {
                                          wrapper: ({ children, className }, b) => {
                                              return (
                                                  <React.Fragment>
                                                      <thead className={className}>{children}</thead>
                                                      <thead>
                                                          <tr style={{}}>
                                                              {props.insertSubHeader?.map((t, idx) => (
                                                                  <th colSpan={t.colSpan} key={idx}>
                                                                      {t.title}
                                                                  </th>
                                                              ))}
                                                          </tr>

                                                          <tr style={{ textAlign: "center" }}>
                                                              {((): React.ReactNode => {
                                                                  const data = props.insertSubHeader?.map((g) => g.data) ?? [];
                                                                  const rows = Utilitario.zip(data);
                                                                  const colSpans = props.insertSubHeader?.map((g) => g.colSpan) ?? [];

                                                                  let k: React.ReactNode[] = [];
                                                                  for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
                                                                      const row = rows[rowIdx];
                                                                      for (let colIdx = 0; colIdx < row.length; colIdx++) {
                                                                          const key = rowIdx * rows.length + colIdx;
                                                                          k.push(
                                                                              <td colSpan={colSpans[colIdx]} key={key}>
                                                                                  {row[colIdx]}
                                                                              </td>,
                                                                          );
                                                                      }
                                                                  }

                                                                  return k;
                                                              })()}
                                                          </tr>
                                                      </thead>
                                                  </React.Fragment>
                                              );
                                          },
                                      },
                                  }
                            : {
                                  table: ResponsiveTableComponent,
                                  header: {
                                      wrapper: ({ children, className }, b) => {
                                          return (
                                              <React.Fragment>
                                                  <thead className={className}>{children}</thead>
                                                  {props.insertSubHeader && (
                                                      <React.Fragment>
                                                          <thead>
                                                              <tr
                                                                  style={{
                                                                      display: "grid",
                                                                      marginTop: "1em",
                                                                      gridTemplateColumns: "1fr ".repeat(props.insertSubHeader?.length ?? 1),
                                                                      textAlign: "center",
                                                                  }}
                                                              >
                                                                  {props.insertSubHeader?.map((t, idx) => <th key={idx}>{t.title}</th>)}

                                                                  {((): React.ReactNode => {
                                                                      const x = Utilitario.zip(props.insertSubHeader?.map((g) => g.data) ?? []);
                                                                      return x?.flatMap((g) => g).map((g, i) => <td key={i}>{g}</td>);
                                                                  })()}
                                                              </tr>
                                                          </thead>
                                                      </React.Fragment>
                                                  )}
                                              </React.Fragment>
                                          );
                                      },
                                      row: (props: React.ComponentProps<typeof ResponsiveRowComponent>) =>
                                          ResponsiveRowComponent({
                                              ...props,
                                              style: {
                                                  ...props.style,
                                              },
                                          }),
                                  },
                                  body: isDataSourceEmpty
                                      ? {}
                                      : {
                                            row: ResponsiveRowComponent,
                                        },
                              }
                    }
                    columns={((): ColumnType<DataIndexType>[] => {
                        return columnsWithClassnameIndicators.filter(shouldDisplayColumn).map((c) => ({
                            ...c,
                            render(value, record, index) {
                                return (
                                    <React.Fragment>
                                        <Flex
                                            vertical
                                            gap={"0.2em"}
                                            style={{
                                                minWidth: props.disableResponsiveness ? (c.minWidth ?? props.minColWidthIfResponsivenessDisabled) : undefined,
                                            }}
                                        >
                                            {!c.noResponsiveTitle &&
                                                props.addTitleOn &&
                                                getHighestBreakpoint(screenSize).level <= breakpointLevels[props.addTitleOn] && (
                                                    <Typography.Text strong style={{ textWrap: "wrap" }}>
                                                        {" "}
                                                        {c.title?.toString()}{" "}
                                                    </Typography.Text>
                                                )}

                                            {(() => {
                                                if (c.render) {
                                                    let rendered = c.render(value, record, index);

                                                    if (
                                                        rendered &&
                                                        (typeof rendered == "string" ||
                                                            typeof rendered == "number" ||
                                                            (typeof rendered == "object" && "type" in rendered))
                                                    ) {
                                                        return rendered;
                                                    }

                                                    return " - ";
                                                }

                                                return <Typography.Text> {value} </Typography.Text>;
                                            })()}
                                        </Flex>
                                    </React.Fragment>
                                );
                            },
                        }));
                    })()}
                    footer={(records) => {
                        if (props.csv?.enabled != true) return props.footer?.(records);

                        return (
                            <React.Fragment>
                                {props.footer && (
                                    <React.Fragment>
                                        {props.footer(records)}
                                        <Divider />
                                    </React.Fragment>
                                )}
                                <div style={{ display: "flex", flexDirection: "row-reverse" }}>
                                    <ExportarComoCSV
                                        nomeDoArquivo={props.csv?.filename ?? "data.csv"}
                                        options={{ emptyFieldValue: "", excelBOM : true, ...props.csv?.options }}
                                        loading={isAcquiringSource}
                                        getData={async () => {
                                            if (props.csv != undefined && "only" in props.csv && props.csv.only != undefined) return props.csv.only;

                                            const tryRendered = props.csv?.enabled && props.csv.tryRendered != false;

                                            const source = await (async () => {
                                                if (props.csv?.enabled && props.csv?.acquireSource != undefined) {
                                                    setIsAcquiringSource(true);
                                                    return await props.csv.acquireSource().then((v) => {
                                                        return v;
                                                    }).finally(() => {
                                                        setIsAcquiringSource(false);
                                                    });
                                                }

                                                return props.dataSource;
                                            })();

                                            if (source == undefined) return [];

                                            const x = columnsWithClassnameIndicators
                                                .filter((col) => !col.csv?.ignore && (tryRendered || !!col.csv?.get || !!col.dataIndex))
                                                .map((col) => {
                                                    const header =
                                                        col.csv?.title ??
                                                        col.title ??
                                                        (Array.isArray(col.dataIndex) ? col.dataIndex.join(".") : col.dataIndex?.toString());

                                                    if (header === undefined || header === null) throw new Error("Cabeçalho vazio");

                                                    const data = (source ?? []).map((m, idx) => {
                                                        const rendered = () => {
                                                            if (!props.csv?.enabled || !tryRendered || !!col.csv?.ignore) return;

                                                            const r = col.render?.(
                                                                find(
                                                                    m,
                                                                    [col.dataIndex].flatMap((y) => y),
                                                                ),
                                                                m,
                                                                idx,
                                                            );

                                                            if (typeof r == "string" || typeof r == "number") {
                                                                return r;
                                                            } else if (typeof r == "object" && React.isValidElement(r)) {
                                                                /* na possibilidade de ser um elemento (como em um <Link>texto</Link>) */
                                                                const t = textContent(r).trim();
                                                                if (t) return t;
                                                            }
                                                        };

                                                        const value =
                                                            col.csv?.get?.(m) ??
                                                            rendered() ??
                                                            find(
                                                                m,
                                                                [col.dataIndex].flatMap((y) => y),
                                                            );

                                                        return value ?? "";
                                                    });

                                                    return { header: header.toString(), data: data };
                                                })
                                                .concat(
                                                    props.csv != undefined && "concat" in props.csv && props.csv.concat != undefined
                                                        ? props.csv.concat(source)
                                                        : [],
                                                );

                                            const n = x.at(0)?.data.length ?? 0;
                                            if (x.find((pair) => pair.data?.length !== n) != undefined) throw new Error("Impossível gerar colunas");

                                            const _data = Array(Math.max(1, n))
                                                .fill(undefined)
                                                .map((_, idx) => {
                                                    const obj = Object.create(null);

                                                    x.forEach((col) => {
                                                        const header = col.header;
                                                        let data = col.data.at(idx);
                                                        
                                                        if (typeof data === "string") data = data.replace(/[\r\n]+/g, ' ');

                                                        obj[header] = data;
                                                    });

                                                    return obj;
                                                });

                                            return _data;
                                        }}
                                    />
                                </div>
                            </React.Fragment>
                        );
                    }}
                ></Table>
            </div>
        </React.Fragment>
    );
};

export default ResponsiveAntdTable;
